/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/naming-convention */
import React, {
  FC,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import { graphql, PageProps } from 'gatsby';
import { useFlags as useLDFlags } from 'gatsby-plugin-launchdarkly';

import {
  EquipmentIcon,
  AvatarInitial,
  CommentItem,
  BackButton,
} from '@lesmills-international/components';

import {
  Layout,
  withPrivateRoute,
  UpgradeTier,
  WorkoutTrackItem,
  PostComment,
  ErrorDialog,
  ConfirmationDialog,
  CommentReplies,
} from '@components/molecules';
import { ShowMore, MultilineText } from '@components/atoms';
import { TIERS } from '@constants';
import { userContext } from '@context';
import { contentViewTracking } from '@src/utils';
import type { WorkoutTrackItemProps } from '@components/molecules/workoutTrackItem';
import {
  useAddFavoriteWorkout,
  useCheckFavoriteContent,
  useRemoveFavoriteWorkout,
  useNewWorkoutComments,
  useGetWorkout,
  useVideoSession,
} from '@hooks';
import {
  isCommentSafeToDisplay,
  canUserDeleteComment,
  getDeleteConfirmationMessage,
} from '../../utils/workoutCommentsHelpers';
import { CHARACTER_LIMITS, ERROR_MESSAGES, USER_DISPLAY } from '../../constants/workout';
import ShowMoreWrapper from '../../components/atoms/showMore/style';
import {
  convertToRelativeTime,
  addErrorInDatadogRum,
  getUrlParamValue,
} from '../../utils/utilities';
import { createEngagementClient, createClient } from '../../../../../src/services/client';
import {
  GET_COMMENTS,
  CREATE_COMMENT,
  REPORT_COMMENT,
  DELETE_COMMENT,
} from '../../engagementGql/comments';
import UPDATE_WORKOUT_LIST from '../../engagementGql/mylist';
import { ReactComponent as ReplyIcon } from '../../assets/icons/reply.svg';
import { ReactComponent as ThreeDotsIcon } from '../../assets/icons/three_dots.svg';
import { ReactComponent as ReportIcon } from '../../assets/icons/report.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import { PRESENTER_AVATAR } from '../../constants';
import COLORS from '../../themes/colors';

import {
  PlayerWrapper,
  PlayerErrorText,
  VideoTitle,
  WorkoutWrapper,
  Description,
  DescriptionText,
  ContentWrapper,
  PageWrapper,
  SectionWrapper,
  Title,
  ContentContainer,
  EquipmentItem,
  InstructorItem,
  TrackContentContainer,
  ShowMoreContainer,
  NoEquipmentText,
  CommentsWrapper,
  PostCommentWrapper,
  NoCommentsWrapper,
  NoCommentsText,
  NoCommentsTitle,
  TopButtonsWrapper,
  MyListButton,
  RepliedCommentWrapper,
  OptionsWrapper,
  DropdownMenu,
  DropdownMenuOverlay,
  MenuItem,
  Spacer,
  SpacerLeft,
  DeleteIconWrapper,
} from '../../styles/workoutPageStyles';
import { vimeoUserIdentity } from '../../utils/segment/segment';

// 🔧 FIX: Graceful MuxPlayer loading with fallback for missing dependency
const MuxPlayer = lazy(() =>
  import('@mux/mux-player-react').catch(() => ({
    default: () => <div>Video player loading...</div>,
  }))
);

export interface WorkoutPageData {
  equipment_title: string;
  no_equipment_text: string;
  instructor_title: string;
  track_title: string;
  moves_title: string;
  presenter_title: string;
  minute_text: string;
  music_recording_text: string;
  show_more_text: string;
  show_less_text: string;
  cancel_text: string;
  input_placeholder_text: string;
  comments_text: string;
  post_comment_text: string;
  no_comments_title: string;
  no_comments_text: string;
  end_of_comments: string;
  add_to_list: string;
  remove_from_list: string;
  // Error messages (Prismic-based for i18n support)
  error_unauthorized_delete?: string;
  error_delete_generic?: string;
  error_create_generic?: string;
  error_report_generic?: string;
  error_network?: string;
  error_inappropriate_content?: string;
  // Delete confirmation messages
  delete_comment_confirmation?: string;
  delete_reply_confirmation?: string;
  // UI helper text
  multiline_helper_text?: string;
}

const CONTENT_TYPE = 'workout';

export const getInstructorsWithAvatar = (
  instructorTeam: string[],
  displayAllInstructors: boolean
): JSX.Element[] => {
  const instructorsWithAvatar: string[] = [];
  const instructorsWithoutAvatar: string[] = [];

  instructorTeam.forEach((item) => {
    const instructorAvatarUrl = PRESENTER_AVATAR.find(
      (avatar) => avatar?.name.toLowerCase() === item?.toLowerCase()
    )?.image;

    if (instructorAvatarUrl) {
      instructorsWithAvatar.push(item);
    } else {
      instructorsWithoutAvatar.push(item);
    }
  });

  instructorsWithAvatar.sort();
  instructorsWithoutAvatar.sort();

  const sortedInstructors: string[] = [...instructorsWithAvatar, ...instructorsWithoutAvatar];

  const firstFiveInstructors =
    sortedInstructors && sortedInstructors?.length > 5
      ? sortedInstructors.slice(0, 5)
      : sortedInstructors;

  const instructorsToShow = displayAllInstructors ? sortedInstructors : firstFiveInstructors;

  return instructorsToShow.map((item) => {
    const defaultAvatar = <AvatarInitial name={item} />;
    const instructorAvatarUrl = PRESENTER_AVATAR.find(
      (avatar) => avatar?.name.toLowerCase() === item?.toLowerCase()
    )?.image;

    return (
      <InstructorItem key={item} role="listitem">
        {instructorAvatarUrl ? (
          <img
            width="40px"
            height="40px"
            src={instructorAvatarUrl}
            alt="workout-presenter-avatar"
          />
        ) : (
          defaultAvatar
        )}
        {item}
      </InstructorItem>
    );
  });
};

const Component = ({
  workoutName,
  workoutPageData,
  vimeoUserId,
}: {
  workoutName: string;
  workoutPageData: WorkoutPageData;
  vimeoUserId: string | null;
}) => {
  const { data, loading: isWorkoutLoading, error } = useGetWorkout(workoutName);

  const description = data?.description || '';
  const imageWide = data?.imageWide;
  const title = data?.workoutName;
  const workoutTiers = data?.tiers;
  const equipments = data?.equipment;
  const tracksData = data?.tracks;
  const programName = data?.programName;
  const videoId = data?.vimeoId;
  const contentAssetId = data?.assets?.find((asset) => asset.workoutId)?.workoutId ?? videoId;
  const instructorTeam = tracksData
    ? tracksData.reduce((allPresenters: string[], track) => {
        const trackPresenters = track.presenters || [];
        trackPresenters.forEach((presenter: string) => {
          if (!allPresenters.includes(presenter)) {
            allPresenters.push(presenter);
          }
        });
        return allPresenters;
      }, [])
    : [];
  const duration = data?.durationInSeconds;
  const muxAsset = data?.assets?.find((asset) => asset.provider.toLowerCase() === 'mux');
  const parsedMetaData = muxAsset?.metaData ? JSON.parse(muxAsset.metaData) : null;
  const muxVideoToken = getUrlParamValue(muxAsset?.streamUrl, 'token');
  const muxStoryboardToken = getUrlParamValue(parsedMetaData?.muxStoryboardUrl, 'token');
  const muxThumbnailToken = getUrlParamValue(parsedMetaData?.muxThumbnailUrl, 'token');

  const isWorkoutDataLoaded = Boolean(programName && workoutName);

  const [isProcessing, setIsProcessing] = useState(false);

  // Get all feature flags in one call
  const { rtMyWorkouts, rtNewWorkoutComments } = useLDFlags();

  // Use custom hook for new workout comments API functionality
  const newWorkoutComments = useNewWorkoutComments({
    workoutName,
    givenName: givenName || '',
    familyName: familyName || '',
    workoutPageData,
  });

  // Legacy comments state (only used when rtNewWorkoutComments = false)
  const [legacyComments, setLegacyComments] = useState([]);
  const [legacyNextToken, setLegacyNextToken] = useState(null);
  const [legacyLoadingNextPage, setLegacyLoadingNextPage] = useState(false);

  // Use either new hook state or legacy state based on feature flag
  const comments = rtNewWorkoutComments ? newWorkoutComments.comments : legacyComments;
  const nextToken = rtNewWorkoutComments ? newWorkoutComments.nextToken : legacyNextToken;

  const pageSize = 20;
  const [isLoading, setIsLoading] = useState(false);

  // Throttle mechanism for legacy scroll handler
  const scrollThrottleRef = useRef(false);

  const handleCommentsQuery = () => {
    if (rtNewWorkoutComments) {
      // Use new hook for new API
      newWorkoutComments.loadComments();
    } else {
      // Legacy API - preserve original behavior
      createEngagementClient
        .query({
          query: GET_COMMENTS,
          variables: {
            videoId,
            pageSize,
          },
        })
        .then((res) => {
          if (res) {
            const { items, nextToken: firstToken } = res.data.comments;
            setLegacyComments(items);
            setLegacyNextToken(firstToken);
          }
        })
        .catch((err) => {
          addErrorInDatadogRum(err);
        });
    }
  };

  useEffect(() => {
    if (videoId) {
      handleCommentsQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  const handleLoadMore = async () => {
    if (rtNewWorkoutComments) {
      // Use new hook for new API
      newWorkoutComments.loadMoreComments();
    } else {
      // Legacy API pagination
      setLegacyLoadingNextPage(true);
      if (!legacyNextToken) {
        setLegacyLoadingNextPage(false);
        return;
      }

      try {
        const newData = await createEngagementClient.query({
          query: GET_COMMENTS,
          variables: {
            videoId,
            pageSize,
            nextToken: legacyNextToken,
          },
        });

        const responseData = newData.data.comments;
        setLegacyNextToken(responseData.nextToken);
        const newItems = responseData.items;
        setLegacyComments((prevComments) => {
          // Add same deduplication for legacy API to be safe
          const existingIds = new Set(prevComments.map((comment) => comment.id));
          const uniqueNewItems = newItems.filter((item) => !existingIds.has(item.id));

          return [...prevComments, ...uniqueNewItems];
        });
      } catch (loadmoreError) {
        addErrorInDatadogRum(loadmoreError);
      } finally {
        setLegacyLoadingNextPage(false);
      }
    }
  };

  const handleScroll = () => {
    // Legacy scroll handler (new API handled by hook)
    if (rtNewWorkoutComments) return;

    // Ultra-responsive throttle for buttery smooth experience
    if (scrollThrottleRef.current) return;
    scrollThrottleRef.current = true;

    // Use requestAnimationFrame for 60fps smoothness
    requestAnimationFrame(() => {
      scrollThrottleRef.current = false;
    });

    // Ultra-aggressive bottom detection for seamless UX
    const scrollPosition = window.innerHeight + window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;
    const threshold = 1500; // Huge threshold - trigger way before user reaches bottom

    if (scrollPosition >= documentHeight - threshold) {
      // Only load more if we're not already loading AND there are more comments to load
      if (!legacyLoadingNextPage && legacyNextToken) {
        handleLoadMore();
      }
    }
  };

  useEffect(() => {
    // Only add legacy scroll listener if not using new API
    if (!rtNewWorkoutComments) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [legacyNextToken, legacyLoadingNextPage, rtNewWorkoutComments]);

  useEffect(() => {
    if (workoutName && programName) {
      contentViewTracking(workoutName, 'video', programName, `Workout Page - ${workoutName}`);
    }
  }, [programName]);

  const {
    equipment_title,
    no_equipment_text,
    instructor_title,
    track_title,
    show_more_text,
    show_less_text,
    cancel_text,
    input_placeholder_text,
    post_comment_text,
    comments_text,
    no_comments_title,
    no_comments_text,
    end_of_comments,
    add_to_list,
    remove_from_list,
  } = workoutPageData || {}; // 🔧 FIX: Handle undefined workoutPageData gracefully

  const { tier, givenName, familyName, workoutFavorites } = useContext(userContext);

  const [displayedAllTracks, setDisplayedAllTracks] = useState(false);
  const [displayAllInstructors, setDisplayAllInstructors] = useState(false);
  const [displayedTrackData, setDisplayedTrackData] = useState([]);
  const [showDescriptionShowMoreBtn, setShowDescriptionShowMoreBtn] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(true);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [clickedCommentId, setClickedCommentId] = useState('');
  const [clickedComment, setClickedComment] = useState(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isReplyBeingDeleted, setIsReplyBeingDeleted] = useState(false); // Track if deleting a reply vs main comment
  const [muxVideoError, setMuxVideoError] = useState(false);

  // Use either new hook error state or legacy error state based on feature flag
  const isErrorDialogOpen = rtNewWorkoutComments ? newWorkoutComments.isErrorDialogOpen : false;
  const errorMessage = rtNewWorkoutComments ? newWorkoutComments.errorMessage : '';

  const [showThreeDotsMenu, setShowThreeDotsMenu] = useState(false);

  // 🎯 OPTIMIZATION: Click outside to close dropdown + Keyboard navigation (NEW API only)
  useEffect(() => {
    if (!rtNewWorkoutComments || !showThreeDotsMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Close if clicking outside dropdown and trigger button, but NOT on dialogs or reply buttons
      if (
        !target.closest('.dropdown-trigger') &&
        !target.closest('[data-dropdown-overlay]') &&
        !target.closest('[role="dialog"]') && // Don't close when clicking on confirmation/error dialogs
        !target.closest('.MuiDialog-root') && // Don't close when clicking on MUI dialogs
        !target.closest('[data-reply-button]') // Don't interfere with reply button clicks
      ) {
        setShowThreeDotsMenu(false);
        // 🔧 FIX: Only clear clickedCommentId if not replying
        if (!showReplyInput) {
          setTimeout(() => setClickedCommentId(''), 100); // Small delay to allow dialog actions
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowThreeDotsMenu(false);
        // Don't clear reply state on Escape - let user manually cancel
        if (!showReplyInput) {
          setClickedCommentId('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [rtNewWorkoutComments, showThreeDotsMenu, showReplyInput]);

  // 🎯 OPTIMIZATION: All memoized handlers (must be before early returns)
  const handleShowCommentMenu = useCallback(
    (id: string) => {
      if (!id) return;
      setClickedCommentId(id);
      setShowThreeDotsMenu(!showThreeDotsMenu);
    },
    [showThreeDotsMenu]
  );

  const handleOpenDeleteDialog = useCallback(() => {
    if (!clickedCommentId) return;

    // 🔧 FIX: Close dropdown menu immediately when confirmation dialog opens
    setShowThreeDotsMenu(false);

    if (rtNewWorkoutComments) {
      const isReply = comments.some((comment) =>
        comment.repliedComments?.some((reply: any) => reply.id === clickedCommentId)
      );
      setIsReplyBeingDeleted(isReply);
    } else {
      setIsReplyBeingDeleted(false);
    }
    setIsDeleteDialogOpen(true);
  }, [clickedCommentId, rtNewWorkoutComments, comments]);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setShowThreeDotsMenu(false);
  }, []);

  const handleCloseErrorDialog = useCallback(() => {
    if (rtNewWorkoutComments) {
      newWorkoutComments.closeErrorDialog();
    }
  }, [rtNewWorkoutComments, newWorkoutComments]);

  const searchParams = new URLSearchParams(window.location.search);
  const planId = searchParams.get('planId');

  // Owns per-play session identity + lifecycle for the Mux player.
  // See Confluence "LM+ Frontend Session Management".
  const videoSession = useVideoSession({
    videoId,
    contentAssetId,
    duration,
    workoutName,
    planId,
  });

  const matchingWorkout = workoutFavorites?.find((item) => item.workoutName === workoutName);
  const { addFavoriteWorkout } = useAddFavoriteWorkout();
  const { removeFavoriteWorkout } = useRemoveFavoriteWorkout();

  // Only create contentId when both programName and workoutName are initalised
  const workoutContentId = programName && workoutName ? `${programName}|${workoutName}` : '';
  const { isFavorited: isWorkoutFavorited } = useCheckFavoriteContent({
    contentId: workoutContentId,
    contentType: CONTENT_TYPE,
  });

  const [isFavourited, setIsFavourited] = useState(
    rtMyWorkouts ? isWorkoutFavorited : !!matchingWorkout
  );

  const workoutDescriptionRef = useRef<HTMLDivElement>(null);
  const muxPlayerRef = useRef<HTMLVideoElement>(null);
  const workoutDescriptionHeight = workoutDescriptionRef?.current?.clientHeight;

  const showTeaseMessage =
    tier === TIERS.BASE && workoutTiers?.length === 1 && workoutTiers.includes(TIERS.PREMIUM);

  useEffect(() => {
    if (rtMyWorkouts) {
      setIsFavourited(isWorkoutFavorited);
    }
  }, [isWorkoutFavorited]);

  useEffect(() => {
    if (workoutDescriptionHeight && workoutDescriptionHeight > 72) {
      setShowDescriptionShowMoreBtn(true);
      setShowFullDescription(false);
    }
  }, [workoutDescriptionHeight]);

  useEffect(() => {
    if (tracksData && tracksData?.length) {
      const sortedTracksData = tracksData?.slice().sort((a, b) => a.trackNumber - b.trackNumber);
      const first3TrackData =
        tracksData?.length > 3 ? sortedTracksData.slice(0, 3) : sortedTracksData;
      setDisplayedTrackData(displayedAllTracks ? sortedTracksData : first3TrackData);
    }
  }, [displayedAllTracks, tracksData]);

  const handleOnClickTrackItem = (startTime: number) => {
    if (muxPlayerRef.current) {
      muxPlayerRef.current.currentTime = startTime;
      muxPlayerRef.current.play();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  // 🔧 FIX: Don't block page render during loading - show page with loading states
  // if (loading) return null;

  if (error) {
    addErrorInDatadogRum(error);
    return null;
  }

  const handlePostComment = async (comment: string) => {
    if (rtNewWorkoutComments) {
      // Use new hook for new API
      newWorkoutComments.createComment(comment);
    } else {
      // Legacy API logic
      const tempCommentId = legacyComments.length + 1;
      const newComment = {
        id: tempCommentId,
        user: { name: `${givenName} ${familyName}` },
        timestamp: new Date().toISOString(),
        content: comment,
      };
      setLegacyComments((prevComments) => [newComment, ...prevComments]);

      try {
        await createEngagementClient.mutate({
          mutation: CREATE_COMMENT,
          variables: {
            videoId,
            content: comment,
          },
        });
      } catch (err) {
        addErrorInDatadogRum(err);
      }
    }
  };

  const handleDeleteComment = async () => {
    // 🔧 FIX: Validate ID and close confirmation dialog immediately
    if (!clickedCommentId) {
      handleCloseDeleteDialog();
      return;
    }

    setIsLoading(true);

    try {
      if (rtNewWorkoutComments) {
        // Use new hook for new API
        const result = await newWorkoutComments.deleteComment(clickedCommentId);
        if (result.success) {
          handleCloseDeleteDialog();
          setShowThreeDotsMenu(false);
        }
        setIsLoading(false);
      } else {
        // Legacy API logic
        await createEngagementClient.mutate({
          mutation: DELETE_COMMENT,
          variables: { id: clickedCommentId },
        });

        const updatedComments = legacyComments.filter((item) => item.id !== clickedCommentId);
        setLegacyComments(updatedComments);
        handleCloseDeleteDialog();
        setShowThreeDotsMenu(false);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      addErrorInDatadogRum(err);
      handleCloseDeleteDialog();
    }
  };

  const handleReportComment = async () => {
    setIsLoading(true);

    try {
      await createEngagementClient.mutate({
        mutation: REPORT_COMMENT,
        variables: { id: clickedCommentId },
      });
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      addErrorInDatadogRum(err);
    }
    handleCloseReportDialog();
    setShowThreeDotsMenu(false);
  };

  // New function for workout comment replies using real backend IDs
  const handleWorkoutReply = async (comment: string, parentComment: any) => {
    if (rtNewWorkoutComments) {
      // Use new hook for new API
      newWorkoutComments.createReply(comment, parentComment);
      setShowReplyInput(false);
    }
  };

  // Keep existing function for backwards compatibility (old API)
  const handleReply = async (comment: string, parentId: string) => {
    const updatedComments = legacyComments.map((parentComment) => {
      if (parentComment.id === parentId) {
        const newReply = {
          id: parentId + 1,
          user: { name: `${givenName} ${familyName}` },
          timestamp: new Date().toISOString(),
          content: comment,
        };
        return {
          ...parentComment,
          repliedComments: parentComment.isReplied
            ? [...parentComment.repliedComments, newReply]
            : [newReply],
          isReplied: true,
        };
      }
      return parentComment;
    });
    setLegacyComments(updatedComments);
    setShowReplyInput(false);

    try {
      await createEngagementClient.mutate({
        mutation: CREATE_COMMENT,
        variables: {
          videoId,
          content: comment,
          replyTo: parentId,
        },
      });
    } catch (err) {
      addErrorInDatadogRum(err);
    }
  };

  const handleShowReply = (comment: any) => {
    // 🔧 FIX: Explicit state management to prevent toggle conflicts
    const isCurrentlyReplying = clickedCommentId === comment.id && showReplyInput;

    if (isCurrentlyReplying) {
      // If already replying to this comment, close the reply input
      setShowReplyInput(false);
      setClickedCommentId('');
      setClickedComment(null);
    } else {
      // Open reply input for this comment
      setClickedCommentId(comment.id);
      setClickedComment(comment);
      setShowReplyInput(true);
      // 🔧 FIX: Explicitly close three dots menu when opening reply
      setShowThreeDotsMenu(false);
    }
  };

  const COMMENTS_MODERATION_FF = process.env.GATSBY_RT_16_04_2024_COMMENTS_MODERATION === 'true';

  const handleOpenReportDialog = () => {
    // 🔧 FIX: Close dropdown menu immediately when report dialog opens
    setShowThreeDotsMenu(false);
    setIsReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setIsReportDialogOpen(false);
    setShowThreeDotsMenu(false);
  };

  const handleUpdateMylist = async () => {
    if (!isWorkoutDataLoaded) return;

    setIsProcessing(true);
    try {
      if (rtMyWorkouts) {
        if (isFavourited) {
          await removeFavoriteWorkout({ programName, workoutName });
          setIsFavourited(false);
        } else {
          await addFavoriteWorkout({ programName, workoutName });
          setIsFavourited(true);
        }
      } else {
        await createClient.mutate({
          mutation: UPDATE_WORKOUT_LIST,
          variables: {
            input: {
              workoutName,
              program: programName,
              condition: !isFavourited,
            },
          },
        });
        setIsFavourited(!isFavourited);
      }

      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      addErrorInDatadogRum(err);
    }
  };

  const handleCancelReply = () => {
    // 🔧 FIX: Clear all reply-related state when canceling
    setShowReplyInput(false);
    setClickedCommentId('');
    setClickedComment(null);
  };

  const handleCancelComment = () => {};

  return (
    <Layout metaData={{ title }}>
      <PageWrapper>
        <WorkoutWrapper>
          <TopButtonsWrapper>
            <BackButton
              onClick={() => {
                window.history.back();
              }}
              theme="dark"
            />
            {tier === TIERS.PREMIUM && isWorkoutDataLoaded && (
              <MyListButton
                isFavourited={isFavourited}
                isProcessing={isProcessing}
                onClick={() => handleUpdateMylist()}
                text={isFavourited ? remove_from_list : add_to_list}
              />
            )}
          </TopButtonsWrapper>
          {showTeaseMessage ? (
            <UpgradeTier />
          ) : (
            <>
              <PlayerWrapper>
                {muxVideoError || (!isWorkoutLoading && !parsedMetaData?.playbackId) ? (
                  <PlayerErrorText>{ERROR_MESSAGES.PLAYER_LOAD_FAILED}</PlayerErrorText>
                ) : parsedMetaData?.playbackId ? (
                  <Suspense fallback={<div>Loading video player...</div>}>
                    <MuxPlayer
                      ref={muxPlayerRef}
                      playbackId={parsedMetaData?.playbackId}
                      preferPlayback="mse"
                      playbackRates={[0.5, 0.75, 1, 1.25, 1.5, 2]}
                      defaultHiddenCaptions
                      metadata={{
                        video_id: parsedMetaData?.playbackId,
                        video_title: muxAsset?.workoutName,
                        viewer_user_id: vimeoUserId,
                        player_name: 'lesmills-web',
                      }}
                      tokens={{
                        playback: muxVideoToken,
                        thumbnail: muxThumbnailToken,
                        storyboard: muxStoryboardToken,
                      }}
                      accentColor={COLORS.lesmillsGreen}
                      style={{ width: '100%', height: '100%' }}
                      poster={imageWide} // TODO: custimize the poster image by backend
                      onWaiting={(event: any) => videoSession.onWaiting(event?.target?.currentTime)}
                      onPlaying={(event: any) => videoSession.onPlaying(event?.target?.currentTime)}
                      onTimeUpdate={(event: any) =>
                        videoSession.onTimeUpdate(event?.target?.currentTime)
                      }
                      onPause={(event: any) => videoSession.onPause(event?.target?.currentTime)}
                      onSeeking={(event: any) => videoSession.onSeeking(event?.target?.currentTime)}
                      onSeeked={(event: any) => videoSession.onSeeked(event?.target?.currentTime)}
                      onPlay={(event: any) => videoSession.onPlay(event?.target?.currentTime)}
                      onEnded={(event: any) => videoSession.onEnded(event?.target?.currentTime)}
                      onError={(event: any) => {
                        setMuxVideoError(true);
                        addErrorInDatadogRum(event?.detail);
                      }}
                    />
                  </Suspense>
                ) : null}
              </PlayerWrapper>
              <ContentWrapper>
                <VideoTitle role="heading" aria-level={1}>
                  {title}
                </VideoTitle>
                <>
                  {description && (
                    <Description data-testid="Description">
                      <DescriptionText
                        ref={workoutDescriptionRef}
                        data-showfulldescription={showFullDescription}
                      >
                        {description}
                      </DescriptionText>
                      {showDescriptionShowMoreBtn && (
                        <ShowMore
                          showMoreText={show_more_text}
                          showLessText={show_less_text}
                          onClick={(opened) => {
                            setShowFullDescription(!opened);
                          }}
                        />
                      )}
                    </Description>
                  )}
                  <SectionWrapper>
                    <Title role="heading" aria-level={2}>
                      {equipment_title}
                    </Title>
                    <ContentContainer role="list" aria-label="Equipment list">
                      {equipments && equipments.length > 0 ? (
                        equipments.map((item: string) => (
                          <EquipmentItem role="listitem" key={item}>
                            <EquipmentIcon equipmentIconName={item} />
                            {item}
                          </EquipmentItem>
                        ))
                      ) : (
                        <NoEquipmentText>{no_equipment_text}</NoEquipmentText>
                      )}
                    </ContentContainer>
                  </SectionWrapper>
                  {instructorTeam && instructorTeam.length > 0 && (
                    <SectionWrapper>
                      <Title role="heading" aria-level={2}>
                        {instructor_title}
                      </Title>
                      <ContentContainer role="list" aria-label="Instructor list">
                        {getInstructorsWithAvatar(instructorTeam, displayAllInstructors)}
                        {instructorTeam?.length > 5 && (
                          <ShowMore
                            showMoreText={show_more_text}
                            showLessText={show_less_text}
                            onClick={(opened) => {
                              setDisplayAllInstructors(!opened);
                            }}
                          />
                        )}
                      </ContentContainer>
                    </SectionWrapper>
                  )}
                  {displayedTrackData && displayedTrackData?.length > 0 && (
                    <SectionWrapper>
                      <Title role="heading" aria-level={2}>
                        {track_title}
                      </Title>
                      <TrackContentContainer role="list" aria-label="Track list">
                        {displayedTrackData?.map((item: WorkoutTrackItemProps) => (
                          <WorkoutTrackItem
                            {...item}
                            key={item.trackNumber}
                            role="listitem"
                            prismicData={workoutPageData}
                            handleOnClick={handleOnClickTrackItem}
                          />
                        ))}
                        {!displayedAllTracks && tracksData.length > 3 && (
                          <ShowMoreContainer>
                            <ShowMore
                              showMoreText={show_more_text}
                              onClick={() => {
                                setDisplayedAllTracks(true);
                              }}
                              hoverState={false}
                            />
                          </ShowMoreContainer>
                        )}
                      </TrackContentContainer>
                    </SectionWrapper>
                  )}
                </>
                <SectionWrapper>
                  <Title role="heading" aria-level={2}>
                    {comments_text}
                  </Title>
                  <PostCommentWrapper>
                    <PostComment
                      author={givenName || ''}
                      onPost={handlePostComment}
                      onCancel={handleCancelComment}
                      cancelText={cancel_text}
                      postCommentText={post_comment_text}
                      inputPlaceholderText={input_placeholder_text}
                      validateSpaces={rtNewWorkoutComments} // Enhanced validation only for NEW API
                      showCharacterCounter={rtNewWorkoutComments} // Character counter only for NEW API
                      maxCharacters={CHARACTER_LIMITS.MAX_LENGTH} // Backend limit from constants
                      allowNewLines={rtNewWorkoutComments} // Enable multiline for NEW API
                      prismicData={workoutPageData} // Pass Prismic data for i18n support
                    />
                  </PostCommentWrapper>
                  <CommentsWrapper role="listitem" aria-label="Comment list item">
                    {comments.length !== 0 ? (
                      comments
                        .filter((comment) =>
                          rtNewWorkoutComments ? isCommentSafeToDisplay(comment) : true
                        )
                        .map((comment) => (
                          <>
                            <MultilineText enableMultiline={rtNewWorkoutComments}>
                              <CommentItem
                                key={comment.id}
                                author={comment.user?.name || USER_DISPLAY.ANONYMIZED_USER_NAME}
                                content={comment.content}
                                timestamp={convertToRelativeTime(comment.createdAt)}
                              />
                            </MultilineText>
                            <RepliedCommentWrapper>
                              <OptionsWrapper>
                                <ShowMoreWrapper
                                  hoverState
                                  onClick={() => handleShowReply(comment)}
                                  data-reply-button // 🔧 FIX: Add identifier for click outside handler
                                >
                                  <ReplyIcon />
                                  Reply
                                </ShowMoreWrapper>
                                {COMMENTS_MODERATION_FF &&
                                  (rtNewWorkoutComments
                                    ? canUserDeleteComment(comment, vimeoUserId)
                                    : true) && (
                                    <div
                                      style={{ position: 'relative', display: 'inline-block' }}
                                      className="dropdown-trigger"
                                    >
                                      {' '}
                                      {/* Positioning context for NEW API overlay */}
                                      <ShowMoreWrapper
                                        hoverState
                                        onClick={() => {
                                          if (comment?.id) {
                                            handleShowCommentMenu(comment.id);
                                          }
                                        }}
                                        style={{
                                          // 🎯 OPTIMIZATION: Touch-friendly delete button on mobile
                                          minWidth: 'clamp(32px, 8vw, 44px)', // iOS recommended 44px touch target
                                          minHeight: 'clamp(32px, 8vw, 44px)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <ThreeDotsIcon />
                                      </ShowMoreWrapper>
                                      {/* NEW API: Overlay dropdown positioned relative to this button */}
                                      {rtNewWorkoutComments &&
                                        clickedCommentId === comment.id &&
                                        showThreeDotsMenu && (
                                          <DropdownMenuOverlay data-dropdown-overlay>
                                            {/* Security: Only show delete button if user is comment author */}
                                            {canUserDeleteComment(comment, vimeoUserId) && (
                                              <MenuItem onClick={handleOpenDeleteDialog}>
                                                <DeleteIconWrapper>
                                                  <DeleteIcon />
                                                </DeleteIconWrapper>
                                                Delete
                                              </MenuItem>
                                            )}
                                            {/* No report button for NEW API per security requirements */}
                                          </DropdownMenuOverlay>
                                        )}
                                    </div>
                                  )}
                              </OptionsWrapper>
                              <Spacer />
                              {/* LEGACY API: Original inline dropdown (only for legacy, NEW API uses overlay above) */}
                              {!rtNewWorkoutComments &&
                                clickedCommentId === comment.id &&
                                showThreeDotsMenu && (
                                  <DropdownMenu>
                                    {/* Show delete button for all users (legacy behavior) */}
                                    <MenuItem onClick={handleOpenDeleteDialog}>
                                      <DeleteIconWrapper>
                                        <DeleteIcon />
                                      </DeleteIconWrapper>
                                      Delete
                                    </MenuItem>
                                    {/* Report button kept for legacy API */}
                                    <MenuItem onClick={handleOpenReportDialog}>
                                      <ReportIcon />
                                      <SpacerLeft />
                                      Report
                                    </MenuItem>
                                  </DropdownMenu>
                                )}
                              <CommentReplies
                                comment={comment}
                                rtNewWorkoutComments={rtNewWorkoutComments}
                                COMMENTS_MODERATION_FF={COMMENTS_MODERATION_FF}
                                clickedCommentId={clickedCommentId}
                                showThreeDotsMenu={showThreeDotsMenu}
                                showReplyInput={showReplyInput}
                                givenName={givenName}
                                cancelText={cancel_text}
                                inputPlaceholderText={input_placeholder_text}
                                clickedComment={clickedComment}
                                vimeoUserId={vimeoUserId}
                                prismicData={workoutPageData}
                                handleShowCommentMenu={handleShowCommentMenu}
                                handleOpenDeleteDialog={handleOpenDeleteDialog}
                                handleOpenReportDialog={handleOpenReportDialog}
                                handleWorkoutReply={handleWorkoutReply}
                                handleReply={handleReply}
                                handleCancelReply={handleCancelReply}
                              />
                            </RepliedCommentWrapper>
                          </>
                        ))
                    ) : (
                      <NoCommentsWrapper>
                        <NoCommentsTitle>{no_comments_title}</NoCommentsTitle>
                        <NoCommentsText>{no_comments_text}</NoCommentsText>
                      </NoCommentsWrapper>
                    )}
                  </CommentsWrapper>
                  {!nextToken && comments.length !== 0 && (
                    <NoCommentsWrapper>
                      <NoCommentsText>{end_of_comments}</NoCommentsText>
                    </NoCommentsWrapper>
                  )}

                  {/* Global dialogs - moved outside comment loop to prevent multiple backdrops */}
                  <ConfirmationDialog
                    open={isReportDialogOpen}
                    onClose={handleCloseReportDialog}
                    onConfirm={handleReportComment}
                    message="Report this comment to Les Mills?"
                    confirmText="Report"
                    cancelText="Cancel"
                    isLoading={isLoading}
                  />
                  <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={handleCloseDeleteDialog}
                    onConfirm={handleDeleteComment}
                    message={
                      rtNewWorkoutComments
                        ? getDeleteConfirmationMessage(isReplyBeingDeleted, workoutPageData)
                        : 'Replies to this comment will also be deleted.' // Legacy API: original hardcoded message
                    }
                    confirmText="Delete"
                    cancelText="Cancel"
                    isLoading={isLoading}
                  />
                  <ErrorDialog
                    open={isErrorDialogOpen}
                    onClose={handleCloseErrorDialog}
                    errorMessage={errorMessage}
                  />
                </SectionWrapper>
              </ContentWrapper>
            </>
          )}
        </WorkoutWrapper>
      </PageWrapper>
    </Layout>
  );
};

const WorkoutGetWrapper: FC<PageProps<Queries.GetWorkoutDetailPageQuery>> = ({ data, params }) => {
  const workoutPageData = data?.prismicWorkoutDetailPage?.data;
  const workoutName = params.name;

  const { vimeoUserId } = useContext(userContext);

  useEffect(() => {
    vimeoUserIdentity(vimeoUserId as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔧 FIX: Add safety checks for required data
  if (!workoutName) return null;
  // Allow component to render with empty workoutPageData (it has defaults)

  return (
    <Component
      workoutName={workoutName}
      workoutPageData={workoutPageData}
      vimeoUserId={vimeoUserId}
    />
  );
};

export default withPrismicPreview(withPrivateRoute(WorkoutGetWrapper), [
  {
    repositoryName: process.env.GATSBY_PRISMIC_REPOSITORY_NAME!,
  },
]);

export const query = graphql`
  query GetWorkoutDetailPage {
    prismicWorkoutDetailPage {
      _previewable
      data {
        equipment_title
        no_equipment_text
        instructor_title
        show_less_text
        show_more_text
        track_title
        moves_title
        presenter_title
        minute_text
        music_recording_text
        cancel_text
        input_placeholder_text
        comments_text
        post_comment_text
        no_comments_title
        no_comments_text
        end_of_comments
        add_to_list
        remove_from_list
        error_unauthorized_delete
        error_delete_generic
        error_create_generic
        error_report_generic
        error_network
        error_inappropriate_content
        delete_comment_confirmation
        delete_reply_confirmation
        multiline_helper_text
      }
    }
  }
`;
